import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Receipt, Image as ImageIcon, X, Check, AlertCircle, Pause, Send, Clock } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { AuthImageLightbox } from "@/components/AuthImageLightbox";

const STATUS_STYLES = {
  SUBMITTED: "bg-sky-100 text-sky-700 border-sky-200",
  ON_HOLD: "bg-amber-100 text-amber-700 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
};

const formatCurrency = (paise) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format((paise || 0) / 100);

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ts; }
}

/**
 * Build the approval-timeline entries from an expense doc. Reads the
 * fields set by routes/expenses.py:_finalize (approved_*, rejected_*, hold_*).
 */
function buildTimeline(expense) {
  const entries = [];
  // Submission
  entries.push({
    kind: "SUBMITTED",
    label: "Submitted",
    at: expense.created_at,
    by: expense.employee_name || expense.employee_id || "Employee",
  });
  if (expense.hold_at) {
    entries.push({
      kind: "HOLD",
      label: "Put on hold",
      at: expense.hold_at,
      by: expense.hold_by,
      tag: expense.hold_tag,
      notes: expense.hold_reason,
    });
  }
  if (expense.approved_at) {
    entries.push({
      kind: "APPROVED",
      label: "Approved",
      at: expense.approved_at,
      by: expense.approved_by_name || expense.approved_by,
      tag: expense.approval_tag,
      notes: expense.approval_notes,
    });
  }
  if (expense.rejected_at || (expense.status === "REJECTED" && expense.rejection_reason)) {
    entries.push({
      kind: "REJECTED",
      label: "Rejected",
      at: expense.rejected_at,
      by: expense.rejected_by,
      tag: expense.rejection_tag,
      notes: expense.rejection_reason,
    });
  }
  return entries.sort((a, b) => (new Date(a.at).getTime() || 0) - (new Date(b.at).getTime() || 0));
}

const TIMELINE_ICONS = {
  SUBMITTED: { Icon: Send,        ring: "border-sky-400 bg-sky-50 text-sky-600" },
  HOLD:      { Icon: Pause,       ring: "border-amber-400 bg-amber-50 text-amber-600" },
  APPROVED:  { Icon: Check,       ring: "border-emerald-400 bg-emerald-50 text-emerald-600" },
  REJECTED:  { Icon: AlertCircle, ring: "border-red-400 bg-red-50 text-red-600" },
};

/**
 * <ExpenseDetailDialog>
 * Read-only viewer for an expense report. Shows period summary, item list,
 * attached photos (clickable for lightbox), and a vertical approval timeline.
 */
export function ExpenseDetailDialog({ open, onClose, expense, employee }) {
  const [lightboxPath, setLightboxPath] = useState(null);
  if (!expense) return null;
  const photos = expense.photos || [];
  const items = expense.items || [];
  const timeline = buildTimeline(expense);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="expense-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#8B1A1A]" />
              Expense {expense.expense_id}
              <Badge variant="outline" className={`ml-2 text-xs ${STATUS_STYLES[expense.status] || ""}`}>
                {expense.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Employee</p>
                <p className="text-sm font-medium mt-0.5">{employee?.full_name || expense.employee_id || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Period</p>
                <p className="text-sm font-medium mt-0.5">{expense.period}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Travel</p>
                <p className="text-sm font-medium mt-0.5">{expense.travel_kms || 0} km · {formatCurrency(expense.travel_amount)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Total</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white font-mono mt-0.5">{formatCurrency(expense.total_amount)}</p>
              </div>
            </div>

            {/* Items */}
            {items.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
                  Expense items <span className="text-slate-400 font-normal">({items.length})</span>
                </p>
                <div className="border rounded-md overflow-hidden dark:border-slate-700">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                      <tr>
                        <th className="text-left px-2 py-1.5">Date</th>
                        <th className="text-left px-2 py-1.5">Category</th>
                        <th className="text-left px-2 py-1.5">Description</th>
                        <th className="text-right px-2 py-1.5">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} className="border-t dark:border-slate-700" data-testid={`expense-item-${i}`}>
                          <td className="px-2 py-1.5">{it.date || "—"}</td>
                          <td className="px-2 py-1.5">{it.category || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">{it.description || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{formatCurrency(it.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Photos */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <ImageIcon className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Receipt photos <span className="text-slate-400 font-normal">({photos.length})</span>
                </p>
              </div>
              {photos.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No receipts attached.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="expense-photo-grid">
                  {photos.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightboxPath(p)}
                      className="aspect-square rounded-md overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-[#8B1A1A] transition focus:outline-none focus:ring-2 focus:ring-[#8B1A1A]"
                      data-testid={`expense-photo-${i}`}
                    >
                      <AuthImage path={p} alt={`Receipt ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Timeline */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Approval timeline</p>
              </div>
              <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-2 space-y-3" data-testid="expense-timeline">
                {timeline.map((t, i) => {
                  const meta = TIMELINE_ICONS[t.kind] || TIMELINE_ICONS.SUBMITTED;
                  const Icon = meta.Icon;
                  return (
                    <li key={i} className="ml-4" data-testid={`timeline-${t.kind.toLowerCase()}`}>
                      <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full border-2 ${meta.ring}`}>
                        <Icon className="w-3 h-3" />
                      </span>
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{t.label}</p>
                        <p className="text-[11px] text-slate-500">{fmt(t.at)}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        by <span className="font-medium text-slate-700 dark:text-slate-300">{t.by || "—"}</span>
                        {t.tag ? <> · <Badge variant="outline" className="text-[10px] py-0">{t.tag}</Badge></> : null}
                      </p>
                      {t.notes && <p className="text-xs italic text-slate-600 dark:text-slate-400 mt-0.5">"{t.notes}"</p>}
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={onClose} className="bg-white border-2 border-slate-300 text-slate-700">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightboxPath && (
        <AuthImageLightbox path={lightboxPath} onClose={() => setLightboxPath(null)} />
      )}
    </>
  );
}

export default ExpenseDetailDialog;
